import React, { useRef, useEffect, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Box } from '@mui/material'
import Grid from '@mui/material/Grid2'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useService } from '../ServiceContext'
import ServiceFAQ from './ServiceFAQ'
import ConditionsTreat from './ConditionsTreat'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
gsap.registerPlugin(ScrollTrigger)

const ServiceAbout = () => {
  const { service } = useService()
  const containerRef = useRef(null)
  const rightSectionRef = useRef(null)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    pageUrl: window.location.href,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  //const [submitStatus, setSubmitStatus] = useState(null);
  const navigation = useNavigate()
  // Validation functions
  const validateForm = () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation - exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    const cleanedPhone = formData.phone.trim().replace(/\s/g, '');
    if (!cleanedPhone) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(cleanedPhone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For phone, only allow numbers
    if (name === 'phone') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        Subject: 'Lead From MagnAid Website',
        Data: {
          ...formData,
        },
      };
      const response = await axios.post(
        'https://maxaidapi.azurewebsites.net/api/EmailApi',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          params: {
            code: 'ejJkX3l8qSvgulht0AFLPDySiepq0di50UeRGPa4r5VBAzFudFpISA==',
          },
        }
      );
      const dataMessage = response.data;
      if (dataMessage.status === 'Success') {

        setFormData({ fullName: '', email: '', phone: '', pageUrl: window.location.href, });
        navigation('/thank-you')

      } else {

        toast.error('Something went wrong', dataMessage.message);
      }
    } catch (error) {

      console.error('Failed to send email:', error.response?.data || error.message);
      toast.error(
        'Failed to send email',
        error.response?.data || error.message,
        { style: { color: 'black' } }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      gsap.to(rightSectionRef.current, {
        scrollTrigger: {
          trigger: rightSectionRef.current,
          start: 'top 150px',
          end: 'bottom+=0px bottom',
          scrub: true,
          pin: true
        }
      })
      return () => {
        ScrollTrigger.getAll().forEach(trigger => trigger.kill())
      }
    })
    return () => mm.revert()
  }, [])

  if (!service) {
    return <div>Loading service data...</div>
  }

  return (
    <div
      ref={containerRef}
      className='w-full flex justify-center items-center px-2 md:px-0 py-28'
    >
      <div className='2xl:w-3/4 3xl:w-2/3 xl:w-[93%] w-[95%] h-auto border-b-[1px] md:pb-36 pb-24'>
        <div className='flex lg:flex-row flex-col lg:gap-10 xl:gap-[5.5rem] relative overflow-hidden'>
          {/* Left Section - Scrolls */}
          <div className='xl:w-[58%] lg:w-[55%] w-full flex flex-col'>
            <h2 className='font-heading text-[38px]  leading-[1.316em] mb-6 text-thickGreen'>
              {service.aboutTitle}
            </h2>
            {service.about.map((paragraph, index) => (
              <p
                key={index}
                className='text-[18px] font-body text-paraGreen font-medium leading-[1.667em] mb-4'
              >
                {paragraph}
              </p>
            ))}
            <div className='py-12'>
              <img
                src={service.image}
                className='object-cover rounded-3xl'
                alt='serviceImage-top'
              />
            </div>
            <div>
              <h2 className='font-heading text-[38px]  leading-[1.316em] mb-6 text-thickGreen'>
                {service?.procedures?.title}
              </h2>
              <p className='text-[18px] font-body text-paraGreen  leading-[1.667em] mb-4'>
                {service?.procedures?.description}
              </p>
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={5} className='flex items-center mb-5'>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div>
                      <img
                        src={service.procedures.imageURL}
                        alt=''
                        className='rounded-[40px] object-cover lg:h-[250px] w-full'
                      />
                    </div>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div>
                      <ul className='text-[15px] font-body text-thickGreen font-bold'>
                        {service.procedures.points.map((point, index) => (
                          <li key={index} className='flex gap-2 mb-4'>
                            <CheckBoxIcon />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Grid>
                </Grid>
                <p className='text-[18px] font-body text-paraGreen font-[500] leading-[1.667em] mb-8'>
                  {service.procedures.bottomLine}
                </p>
              </Box>
            </div>
            <ConditionsTreat />
            <ServiceFAQ />
          </div>

          {/* Right Section - Pinned with GSAP */}
          <div ref={rightSectionRef} className='xl:w-[28%] lg:w-[42%] mt-6 md:mt-0'>
            <div className='bg-lightOrange flex flex-col h-fit rounded-3xl p-6 '>
              <h2 className='text-[28px] leading-[1.429em] font-heading mb-4  text-thickGreen'>
                Get in touch with us today
              </h2>
              <p className='text-[16px] font-body font-[500] leading-[1.667em] mb-6 text-paraGreen'>
                Get exceptional care along with personalised treatment for a healthier future at MagnAid Hospitals by scheduling an appointment.
              </p>
              <form onSubmit={handleSubmit}>
                <div>
                  <label className='text-[16px] block font-body font-[700] text-thickGreen leading-[1.112em] mb-2'>
                    Full Name
                  </label>
                  <input
                    type='text'
                    name='fullName'
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`w-full  text-[16px] font-body border-b-[1px] border-thickGreen text-thickGreen bg-transparent transition-all duration-300 outline-none ${errors.fullName ? 'border-red-500' : '= focus:border-thickGreen'
                      }`}
                  />
                  {errors.fullName && <p className='text-red-500 text-sm mt-1 font-body'>{errors.fullName}</p>}
                </div>
                <div className='mt-5'>
                  <label className='text-[16px] block font-body font-[700] text-thickGreen leading-[1.112em] mb-2'>
                    Email Address
                  </label>
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full  text-[16px]  border-b-[1px] border-thickGreen text-thickGreen bg-transparent font-body transition-all duration-300 outline-none ${errors.email ? 'border-red-500' : ' focus:border-thickGreen '
                      }`}
                  />
                  {errors.email && <p className='text-red-500 text-sm mt-1 font-body'>{errors.email}</p>}
                </div>
                <div className='mt-5'>
                  <label className='text-[16px] block font-body font-[700] text-thickGreen leading-[1.112em] mb-2'>
                    Phone Number
                  </label>
                  <input
                    type='tel'
                    name='phone'
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    className={`w-full  text-[18px]  border-b-[1px] border-thickGreen text-thickGreen bg-transparent font-body transition-all duration-300 outline-none ${errors.phone ? 'border-red-500' : ' focus:border-thickGreen'
                      }`}
                  />
                  {errors.phone && <p className='text-red-500 text-sm mt-1 font-body'>{errors.phone}</p>}
                </div>
                <div className='mt-5'>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className={`w-full    text-[18px] py-3 px-6 rounded-[100px] font-medium  border-[1px] text-[#fff] ${isSubmitting ? 'bg-gray-400' : 'bg-orange hover:bg-lightGreen h transition-all hover:duration-300'
                      }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceAbout